import { ICustomerUtilityBillFile } from './interface';
import { IPagination } from '../IPagination';
import { IBatchReparseResult } from '../customer-utility-bill';
import { API } from '../api';

// Data layer for Customer Utility Bill Files — the source documents (PDFs) bills are parsed from.
export interface ICustomerUtilityBillFilesQuery {
    search?: string;
    ids?: string; // CSV of ids — filters to this explicit set, e.g. to poll live status during a batch reparse
    statuses?: string; // CSV of CustomerUtilityBillFileStatus
    sources?: string; // CSV of CustomerUtilityBillSource
    customers?: string; // CSV of normalized customerName values
    sortField?: string;
    sortOrder?: 'ascend' | 'descend';
    offset?: number;
    limit?: number;
}

const JSON_HEADERS = { 'Content-Type': 'application/json' };

// The list needs the source (source / customerName columns) and the bills (number of bills column).
const LIST_INCLUDE = 'source,bills';

const readJsonResponse = async <T>(response: Response, fallbackError: string): Promise<T> => {
    const respBody: any = await response.json();
    if (response.ok) {
        return respBody as T;
    }
    throw new Error(respBody?.error?.message || fallbackError);
};

export const fetchCustomerUtilityBillFiles = async (
    query: ICustomerUtilityBillFilesQuery,
    signal?: AbortSignal | null
): Promise<IPagination<ICustomerUtilityBillFile>> => {
    const path = `/customer-utility-bill-files?${new URLSearchParams({
        include: LIST_INCLUDE,
        ...(query.search && { search: query.search }),
        ...(query.ids && { ids: query.ids }),
        ...(query.statuses && { statuses: query.statuses }),
        ...(query.sources && { sources: query.sources }),
        ...(query.customers && { customers: query.customers }),
        ...(query.sortField && { sortField: query.sortField }),
        ...(query.sortOrder && { sortOrder: query.sortOrder }),
        ...(query.offset != null && { offset: String(query.offset) }),
        ...(query.limit != null && { limit: String(query.limit) }),
    })}`;

    const response = await API.fetch(path, {
        method: 'get',
        headers: JSON_HEADERS,
        ...(signal && { signal }),
    });

    return readJsonResponse<IPagination<ICustomerUtilityBillFile>>(response, `Couldn’t load bill files`);
};

// Distinct filter-option values (from the DB) for the sources / customers sidebar filters.
const fetchFileFilterOptions = async (option: 'sources' | 'customers', signal?: AbortSignal | null): Promise<string[]> => {
    const path = `/filters/options/customer-utility-bill-file/${option}`;

    const response = await API.fetch(path, {
        method: 'get',
        headers: JSON_HEADERS,
        ...(signal && { signal }),
    });

    const options = await readJsonResponse<{ value: string }[]>(response, `Couldn’t load ${option} filter options`);
    return options.map(o => o.value);
};

export const fetchCustomerUtilityBillFileSources = (signal?: AbortSignal | null) => fetchFileFilterOptions('sources', signal);

export const fetchCustomerUtilityBillFileCustomers = (signal?: AbortSignal | null) => fetchFileFilterOptions('customers', signal);

// Request a re-parse of a batch of files: eligible files flip to REPARSE_QUEUED. Missing / non-reparseable /
// failed-to-enqueue ids come back in `skipped`.
export const reparseCustomerUtilityBillFiles = async (ids: string[], signal?: AbortSignal | null): Promise<IBatchReparseResult> => {
    const path = `/customer-utility-bill-files/reparse`;

    const response = await API.fetch(path, {
        ...(signal && { signal }),
        method: 'POST',
        headers: JSON_HEADERS,
        body: JSON.stringify({ ids }),
    });

    return readJsonResponse<IBatchReparseResult>(response, `Couldn’t re-parse the selected files`);
};

// Fetch a single file with its source and bills. Used by the file modal to poll live re-parse status (a re-parse
// runs asynchronously on the backend after the reparse request is accepted).
export const fetchCustomerUtilityBillFileById = async (id: string, signal?: AbortSignal | null): Promise<ICustomerUtilityBillFile> => {
    const path = `/customer-utility-bill-files/${encodeURIComponent(id)}?${new URLSearchParams({ include: LIST_INCLUDE })}`;

    const response = await API.fetch(path, {
        ...(signal && { signal }),
        method: 'get',
        headers: JSON_HEADERS,
    });

    return readJsonResponse<ICustomerUtilityBillFile>(response, `Couldn’t load the bill file`);
};

// Fetch a short-lived (15 min), pre-signed S3 URL for a file's PDF. `mode=view` renders inline; `mode=download`
// is signed as an attachment. The backend returns 404 when the file has no PDF yet (not downloaded) — surfaced
// as `null` so the viewer can show an empty state rather than an error.
export const fetchCustomerUtilityBillFilePdfPreviewUrl = async (
    id: string,
    signal?: AbortSignal | null,
    mode: 'view' | 'download' = 'view'
): Promise<string | null> => {
    const path = `/customer-utility-bill-files/${encodeURIComponent(id)}/preview?mode=${mode}`;

    const response = await API.fetch(path, {
        ...(signal && { signal }),
        method: 'get',
        headers: JSON_HEADERS,
    });

    if (response.status === 404) {
        return null;
    }

    const respBody = await readJsonResponse<{ url: string }>(response, `Couldn’t load the file PDF`);
    return respBody.url;
};
